import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useAppContext } from '@/Main';
import { formatUtcDate } from '@/helpers/formatUtcDate';
import { getColor } from '@/helpers/utils';
import type { SourceInsightsTrendResult } from '../../types';
import { PANEL_FONT_FAMILY } from '../../utils/panel-font';
import { sourceDisplayLabel } from '../../utils/source-insights';

const SERIES_COLORS = ['#7c3aed', '#2563eb', '#059669', '#d97706', '#db2777'];

export function SourceTrendChart({
  trend,
}: {
  trend: SourceInsightsTrendResult | null;
}) {
  const {
    config: { isDark },
  } = useAppContext() as { config: { isDark: boolean } };

  const option = useMemo(() => {
    if (!trend?.series.length) return null;

    const dates = [
      ...new Set(trend.series.flatMap(series => series.data.map(point => point.date))),
    ].sort();

    if (dates.length < 1) return null;

    const labels = dates.map(date => formatUtcDate(date, { preset: 'date' }) || date);

    const axisLabelColor = getColor('gray-700');
    const axisLineColor = getColor('gray-300');
    const splitLineColor = getColor('gray-200');
    const mutedText = getColor('gray-600');
    const tooltipBg = getColor('gray-100');
    const tooltipBorder = getColor('gray-300');
    const tooltipText = getColor('gray-1100');

    return {
      color: SERIES_COLORS,
      tooltip: {
        trigger: 'axis',
        backgroundColor: tooltipBg,
        borderColor: tooltipBorder,
        borderWidth: 1,
        extraCssText: 'direction: rtl; text-align: right;',
        textStyle: { color: tooltipText, fontSize: 12, fontFamily: PANEL_FONT_FAMILY },
        formatter: (
          params: Array<{
            seriesName: string;
            value: number | null;
            axisValue: string;
            dataIndex: number;
            color: string;
          }>,
        ) => {
          if (!params.length) return '';
          const rows = params
            .filter(item => item.value != null)
            .map(item => {
              const series = trend.series.find(
                entry => sourceDisplayLabel(entry.host, entry.displayName) === item.seriesName,
              );
              const point = series?.data[item.dataIndex];
              return `
                <div style="margin-top:6px">
                  <span style="display:inline-block;width:8px;height:8px;border-radius:999px;background:${item.color};margin-inline-end:6px"></span>
                  <strong>${item.seriesName}</strong><br/>
                  <span style="color:${mutedText};padding-inline-start:14px">
                    استفاده ${item.value}% · Citation ${point?.citations ?? 0} · پاسخ ${point?.aiResponses ?? 0}
                  </span>
                </div>`;
            })
            .join('');
          return `<div style="font-family:${PANEL_FONT_FAMILY};font-size:12px;direction:rtl;text-align:right;color:${tooltipText}"><div style="font-weight:600;margin-bottom:2px">${params[0].axisValue}</div>${rows}</div>`;
        },
      },
      legend: { show: false },
      grid: { left: 16, right: 36, top: 12, bottom: 28 },
      xAxis: {
        type: 'category',
        data: labels,
        boundaryGap: false,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisLabel: { color: axisLabelColor, fontSize: 11, fontFamily: PANEL_FONT_FAMILY },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        min: 0,
        max: 100,
        axisLabel: {
          color: axisLabelColor,
          fontSize: 11,
          formatter: '{value}%',
          fontFamily: PANEL_FONT_FAMILY,
        },
        splitLine: { lineStyle: { color: splitLineColor } },
      },
      series: trend.series.map(series => ({
        name: sourceDisplayLabel(series.host, series.displayName),
        type: 'line',
        smooth: true,
        showSymbol: dates.length <= 14,
        symbolSize: 6,
        lineStyle: { width: 2.5 },
        data: dates.map(date => {
          const point = series.data.find(entry => entry.date === date);
          return point?.usedPercent ?? null;
        }),
        connectNulls: false,
      })),
    };
  }, [trend, isDark]);

  if (!option) {
    return (
      <div
        className="d-flex align-items-center justify-content-center fs--1 text-700"
        style={{ height: '16rem' }}
      >
        داده کافی برای نمایش روند وجود ندارد
      </div>
    );
  }

  return (
    <div>
      <ReactECharts
        key={isDark ? 'dark' : 'light'}
        option={option}
        style={{ height: 248, width: '100%' }}
        opts={{ renderer: 'svg' }}
      />
      <div className="mt-3 d-flex flex-wrap align-items-center justify-content-center gap-3 px-1">
        {trend!.series.map((series, index) => {
          const label = sourceDisplayLabel(series.host, series.displayName);
          return (
            <div
              key={series.sourceDomainId ?? series.host ?? label}
              className="d-flex align-items-center gap-2 fs--2 text-700"
              style={{ maxWidth: '100%' }}
              title={label}
            >
              <span
                className="flex-shrink-0 rounded-circle"
                style={{
                  width: '0.5rem',
                  height: '0.5rem',
                  backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length],
                }}
              />
              <span className="text-truncate">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
